import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "default" | "primary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "icon";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly children: ReactNode;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
};

const baseClasses =
  "inline-flex items-center justify-center gap-1.5 rounded-control border font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cta disabled:opacity-50";

const variantClasses = {
  default:
    "border-border bg-surface text-ink hover:border-border-strong hover:bg-panel-muted",
  primary:
    "border-cta bg-cta text-white hover:border-cta-dark hover:bg-cta-dark",
  ghost:
    "border-transparent bg-transparent text-ink-muted hover:bg-gray-100 hover:text-ink",
  danger:
    "border-critical bg-critical text-white hover:border-red-700 hover:bg-red-700",
} as const satisfies Record<ButtonVariant, string>;

const sizeClasses = {
  sm: "px-2.5 py-1.5 text-xs",
  md: "px-3.5 py-2 text-[13px]",
  icon: "h-9 w-9 p-0 text-sm",
} as const satisfies Record<ButtonSize, string>;

export function Button({
  children,
  className = "",
  size = "md",
  type = "button",
  variant = "default",
  ...props
}: ButtonProps) {
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      type={type}
      {...props}
    >
      {children}
    </button>
  );
}
